import { useApiQuery } from '@tasama/api-client-query';
import { useTranslation } from '@tasama/i18n';
import { useIsAuthenticated } from '../stores/auth-store';
import type { ServiceTitlesResponse } from '@tasama/types/domain';

export const serviceTitlesQueryKey = ['home', 'init-cms'] as const;

/**
 * Hook for fetching the flat sysId → display-name catalogue list.
 * GET /home/initialize
 *
 * Prefetched in AppLayout so the map is warm before any child route needs it.
 * Returns the inner `data` record directly for O(1) look-ups at the call site.
 */
export const useServiceTitlesListQuery = () => {
  const { i18n } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const { data, isPending, error } = useApiQuery<ServiceTitlesResponse>(
    [...serviceTitlesQueryKey, i18n.language],
    '/home/initialize',
    {
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: isAuthenticated,
    }
  );

  return {
    serviceTitles: data?.data ?? null,
    // isPending is true until first response (success or error) regardless of
    // whether the fetch has actually started yet — safer gate than isLoading
    // which requires isFetching to be true simultaneously.
    loading: isPending,
    error: error?.message ?? null,
  };
};
